import React from 'react';
import { useLocale } from 'locale';
import { EducationsRoot, EducationsSchoolTable, ExtraInfoRoot, ExtraInfoLabel, ExtraInfoItem, ExtraInfoVal } from '../styled';
import { SchoolUnion } from 'store/sagas/apiService/types';
import { SmallText } from 'ds/components/typography';
import { Link } from 'components/link';
import { Route } from 'config/routes';
import { Pagination } from 'ds/components/pagination';

interface ExtraInfo {
  neighborhoodMadadMadlan?: number;
  madlanEducationIndexCityAverage?: number;
  madlanEducationIndexNationalAverage?: number;
}

interface SchoolsTableProps {
  data: SchoolUnion[];
  extraData?: ExtraInfo;
  showLess: boolean;
  isCity?: boolean;
}

function makeExtraInvoValue(value: number): string {
  return `${value}/100`;
}

const ITEMS_PER_PAGE = 8;

export const SchoolsTable: React.FC<SchoolsTableProps> = ({ data, showLess, extraData, isCity }) => {
  const { t } = useLocale();
  const [ currentPage, setCurrentPage ] = React.useState(1);
  const pages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const showPagination = pages > 1;
  const dataToShow = data.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <EducationsRoot withBottomPadding={!showPagination}>
      {extraData && (Object.keys(extraData).length > 0) ? (
        <ExtraInfoRoot>
          {extraData.neighborhoodMadadMadlan ? (
            <ExtraInfoItem isHighlighted>
              <ExtraInfoLabel isHighlighted>{t('localPage.schoolsTable.madad.label', { isCity })}</ExtraInfoLabel>
              <ExtraInfoVal weight="bold" isHighlighted>{makeExtraInvoValue(extraData.neighborhoodMadadMadlan)}</ExtraInfoVal>
            </ExtraInfoItem>
          ) : null}
          {extraData.madlanEducationIndexCityAverage ? (
            <ExtraInfoItem isHighlighted={isCity}>
              <ExtraInfoLabel isHighlighted={isCity}>{isCity ? t('localPage.schoolsTable.madad.label', { isCity }) : t('widget.ilSchools.cityAvg')}</ExtraInfoLabel>
              <ExtraInfoVal weight="bold" isHighlighted={isCity}>{makeExtraInvoValue(extraData.madlanEducationIndexCityAverage)}</ExtraInfoVal>
            </ExtraInfoItem>
          ) : null}
          {extraData.madlanEducationIndexNationalAverage ? (
            <ExtraInfoItem>
              <ExtraInfoLabel>{t('widget.ilSchools.ilAvg')}</ExtraInfoLabel>
              <ExtraInfoVal weight="bold">{makeExtraInvoValue(Math.round(extraData.madlanEducationIndexNationalAverage))}</ExtraInfoVal>
            </ExtraInfoItem>
          ) : null}
        </ExtraInfoRoot>
      ) : null}
      <EducationsSchoolTable>
        <thead>
          <tr>
            <th><SmallText weight="bold">{t('localPage.schoolsTable.name')}</SmallText></th>
            <th><SmallText weight="bold">{t('localPage.schoolsTable.classRange')}</SmallText></th>
            {showLess ? null : (
              <>
                <th><SmallText weight="bold">{t('localPage.schoolsTable.educationSystem')}</SmallText></th>
                <th><SmallText weight="bold">{t('localPage.schoolsTable.everage.persons')}</SmallText></th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {dataToShow.map(({ schoolId, schoolName, classRange, averageNumberOfStudentsPerClass, supervision }) => (
            <tr key={schoolId}>
              <td width="320px"><SmallText><Link routeName={Route.School} routeParams={{ id: schoolId }} target="_blank">{schoolName}</Link></SmallText></td>
              <td width="55px">{classRange && <SmallText>{classRange}</SmallText>}</td>
              {showLess ? null : (
                <>
                  <td width="90px" data-auto={supervision}>{supervision && <SmallText>{t('schoolPage.sectors.map', { value: supervision })}</SmallText>}</td>
                  <td>{averageNumberOfStudentsPerClass && <SmallText>{averageNumberOfStudentsPerClass}</SmallText>}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </EducationsSchoolTable>
      {showPagination ? (
        <Pagination
          totalPages={pages}
          onSetActivePage={setCurrentPage}
          activePage={currentPage}
          paginationType={'pages'}
        />
      ) : null}
    </EducationsRoot>
  );
};
